import { MdsLink, MDSSectionSlotName, MDSIconButton } from "@/mds";
import { observer } from "mobx-react-lite";
import {
  StyledCardIntegrationInstruction,
  StyledContentList,
  StyledContentSectionTitleWrapper,
  StyledContentStatusWrapper,
  StyledContentWrapper,
  StyledListBox,
} from "@/pages/integrations/shared/integrations.styles";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { windowModule } from "@/modules/window";
import { css } from "@/domains/emotion";
import { IntegrationsLoader } from "@/pages/integrations/shared/integrations-loader";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { IntegrationsOrganizingForChromeExtension } from "@/pages/integrations/chrome-extension/integrations-organizing-for-chrome-extension";
import { useCallback } from "react";
import { clientEnvModule } from "@/modules/client-env";

export const IntegrationsChromeExtensionPageContent = observer(() => {
  const { store } = useAppStore();
  const { isLoading, load } = store.chromeExtension;

  useEffectOnMount(() => {
    load();
  });

  const handlePress = useCallback(
    () => windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.CHROME_EXTENSION) }),
    []
  );

  const isConnected = !!store.chromeExtension.isInstalled.data;

  const items: MDSListBoxItemProps[] = [
    {
      id: "chrome-extension",
      textValue: "Install the Mem Chrome Extension",
      leftAddon: <MdsIcon kind={MdsIconKind.GoogleChrome} />,
      children: <>Install the Mem Chrome Extension</>,
      className: installMemChromeExtensionStyles,
      rightAddon: (
        <MDSIconButton iconKind={MdsIconKind.ArrowUpRightFromSquare} onClick={handlePress} />
      ),
      onAction: handlePress,
    },
  ];

  return (
    <StyledContentWrapper>
      <StyledCardIntegrationInstruction
        title={
          <StyledContentSectionTitleWrapper>
            <MdsIcon kind={MdsIconKind.GoogleChrome} />
            <span>Mem Chrome Extension</span>
          </StyledContentSectionTitleWrapper>
        }
      >
        {!clientEnvModule.isDesktop() && (
          <div slot={MDSSectionSlotName.HeaderRightAddon}>
            <StyledContentStatusWrapper isConnected={isConnected}>
              <span>{isConnected ? "Connected" : "Not connected"}</span>
              <MdsIcon
                kind={isConnected ? MdsIconKind.CheckCircleSolid : MdsIconKind.CircleSolid}
              />
            </StyledContentStatusWrapper>
          </div>
        )}
        <StyledContentList>
          <li>
            Install the <MdsLink onPress={handlePress}>Mem Chrome Extension</MdsLink> and sign in to
            your Mem account
          </li>
          <li>Pin it to your browser window to enable one-click web clipping</li>
          <li>Save entire webpages, or select specific text or images</li>
          <li>Mem will create and organize notes with the original webpages attached</li>
        </StyledContentList>
        {!isConnected && <StyledListBox items={items} aria-label="Install Mem Chrome Extension" />}
      </StyledCardIntegrationInstruction>
      {isLoading ? <IntegrationsLoader /> : <IntegrationsOrganizingForChromeExtension />}
      <IntegrationsLayoutBottomBuffer />
    </StyledContentWrapper>
  );
});

const installMemChromeExtensionStyles = css`
  background-color: white;
  cursor: pointer;
  width: fit-content;

  [slot="left-addon"] {
    align-self: center;
  }

  &:hover:not([data-disabled]) {
    background-color: white !important;
  }

  [slot="right-addon"] {
    button[data-mds-name="button"] {
      transition: none;

      &[data-hovered="true"] {
        background-color: white;
      }
    }
  }
`;
